import React, { memo, useMemo } from "react";
import {
  ColDef,
  RowClassParams,
  RowStyle,
  RowClickedEvent,
} from "ag-grid-community";
import { Control, Controller, useForm } from "react-hook-form";
import { useClientInfo, useClientsState } from "atoms/clientsAtom";
import {
  useAddDiscountItem,
  useDeleteDiscountItem,
  useGetDiscountItems,
} from "features/ClientInfo/api";
import { ClientDiscountForm } from "features/ClientInfo/ClientInfo";
import { useGetProductsGroup } from "features/ProductsGroup/api";
import { ClientDiscountItemGroupItem } from "features/ClientInfo/types";
import { ItemsGroup } from "types";

import { NamedCounter } from "components/NamedCounter/NamedCounter";
import { Table } from "components/Table/Table";
import { Button, EButtonVariant } from "components/Button/Button";
import { NamedSelect } from "components/NamedSelect/NamedSelect";

import styles from "../../styles.module.css";

export type ClientInfoDiscountForm = {
  selectedProductGroup: any;
  discount: string;
};

const FIELDS = [
  {
    title: "Начальный % скидки",
    varName: "startpercent",
    symbol: "%",
    titleWidth: 140,
  },
  {
    title: "Шаг скидки",
    varName: "shiftpercent",
    symbol: "",
    titleWidth: 130,
  },
  {
    title: "Период увеличения скидки",
    varName: "shiftpercentsum",
    symbol: "",
    titleWidth: 200,
  },
  {
    title: "Текущий % скидки",
    varName: "discountpercent",
    symbol: "%",
    titleWidth: 140,
  },
  {
    title: "Конечный % скидки",
    varName: "endpercent",
    symbol: "%",
    titleWidth: 130,
  },
  {
    title: "Следующее увеличение скидки",
    varName: "nextdiscountsum",
    symbol: "",
    titleWidth: 200,
  },
];

type Props = {
  clientDiscountControl: Control<ClientDiscountForm>;
};

export const Discounts = memo(
  ({ clientDiscountControl }: Props): JSX.Element => {
    const { clientInfoState, setClientInfoState } = useClientInfo();
    const { clientsState } = useClientsState();

    const { mutate: deleteDiscountItem } = useDeleteDiscountItem();

    const { mutate: addDiscountItem } = useAddDiscountItem();

    const { data: productGroupsList = [] } = useGetProductsGroup({
      enabled: true,
    });

    const colDefs: ColDef<ClientDiscountItemGroupItem>[] = [
      {
        headerName: "№",
        field: "clientdiscountitemgroupid",
        resizable: false,
        width: 80,
        flex: 1,
      },
      {
        headerName: "Группа товаров",
        field: "itemgroupid",
        resizable: false,
        flex: 5,
        valueFormatter: (data) => {
          return productGroupsList.find(
            (group: ItemsGroup) =>
              Number(group.itemgroupid) === Number(data.value)
          )?.name;
        },
      },
      { headerName: "Скидка", field: "value", resizable: false, flex: 1 },
    ];

    const { data: discountItems = [] } = useGetDiscountItems(
      clientsState.activeItem?.clientid || ""
    );

    const { handleSubmit, control, reset } = useForm<ClientInfoDiscountForm>({
      defaultValues: {
        selectedProductGroup: null,
        discount: "",
      },
    });

    const productGroupOptions = useMemo(() => {
      return productGroupsList.map((productGroup: any) => ({
        value: productGroup.itemgroupid,
        label: productGroup.name,
      }));
    }, [productGroupsList]);

    const getRowStyle = (
      params: RowClassParams<ClientDiscountItemGroupItem>
    ): RowStyle | undefined => {
      if (
        params.data &&
        clientInfoState.activeDiscountItem?.clientdiscountitemgroupid ===
          params.data.clientdiscountitemgroupid
      ) {
        return {
          backgroundColor: "#f7f4fd",
        };
      }
    };

    const handleRowClick = (e: RowClickedEvent<any, any>) => {
      setClientInfoState((prev) => ({
        ...prev,
        activeDiscountItem:
          prev.activeDiscountItem?.clientdiscountitemgroupid ===
          e.data.clientdiscountitemgroupid
            ? null
            : e.data,
      }));
    };

    const handleDeleteClick = () => {
      const { activeDiscountItem } = clientInfoState;

      if (activeDiscountItem) {
        deleteDiscountItem(activeDiscountItem.clientdiscountitemgroupid, {
          onSuccess: () => {
            setClientInfoState((prev) => ({
              ...prev,
              activeDiscountItem: null,
            }));
          },
        });
      }
    };

    const handleAddClick = () => {
      if (!clientInfoState.showAddDiscountFields) {
        setClientInfoState((prev) => ({
          ...prev,
          activeDiscountItem: null,
          showAddDiscountFields: true,
        }));
      }
    };

    const handleSaveClick = (data: ClientInfoDiscountForm) => {
      addDiscountItem(
        {
          itemgroupid: data.selectedProductGroup?.value,
          value: data.discount,
          clientid: clientsState.activeItem?.clientid || "",
          importcode: "",
        },
        {
          onSuccess: () => {
            setClientInfoState((prev) => ({
              ...prev,
              showAddDiscountFields: false,
            }));
            reset();
          },
        }
      );
    };

    return (
      <>
        <div className="flex flex-wrap mt-2 gap-x-1 gap-y-4 justify-center">
          {FIELDS.map((currentField) => (
            <div key={currentField.title} className="flex ml-5 items-center">
              <Controller
                name={currentField.varName as keyof ClientDiscountForm}
                control={clientDiscountControl}
                render={({ field }) => (
                  <NamedCounter
                    {...field}
                    title={currentField.title}
                    value={field.value as string}
                    symbol={currentField.symbol}
                    titleWidth={currentField.titleWidth}
                  />
                )}
              />
            </div>
          ))}
        </div>
        <div className={styles.clientDataWrapper}>
          <h3 className={styles.table_header}>Скидки по группам товаров</h3>
          <Table
            height={200}
            rowData={discountItems}
            colDefs={colDefs}
            getRowStyle={getRowStyle}
            onRowClicked={handleRowClick}
          />
          {clientInfoState.showAddDiscountFields && (
            <div className={styles.discount_fields}>
              <Controller
                name="selectedProductGroup"
                control={control}
                render={({ field }) => (
                  <NamedSelect
                    {...field}
                    options={productGroupOptions}
                    placeholder="Группа товаров "
                    name=""
                  />
                )}
              />
              <Controller
                name="discount"
                control={control}
                render={({ field }) => (
                  <NamedCounter
                    {...field}
                    title=""
                    symbol="%"
                    style={{ border: 0, outline: 0 }}
                  />
                )}
              />
            </div>
          )}
          <div className="flex items-center">
            {clientInfoState.showAddDiscountFields ? (
              <Button
                icon="galochka"
                className="mr-2"
                onClick={handleSubmit(handleSaveClick)}
                variant={EButtonVariant.SECONDARY}
              >
                Сохранить
              </Button>
            ) : (
              <Button icon="plus" className="mr-2" onClick={handleAddClick}>
                Добавить
              </Button>
            )}
            <Button icon="delete" onClick={handleDeleteClick}>
              Удалить
            </Button>
          </div>
        </div>
      </>
    );
  }
);
